import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MenuItem } from "@/types/menu";
import { Order } from "@/types/orders";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PublicMenuHeader } from "@/components/menu/public/PublicMenuHeader";
import { PublicMenuError } from "@/components/menu/public/PublicMenuError";
import { PublicMenuLoading } from "@/components/menu/public/PublicMenuLoading";
import { PublicMenuTabs } from "./PublicMenuTabs";
import { MenuContentTab } from "./MenuContentTab";
import { OrderContentTab } from "./OrderContentTab";
import { MenuItemDetailsDialog } from "@/components/menu/public/MenuItemDetailsDialog";
import { TagsFilterDialog } from "@/components/menu/public/TagsFilterDialog";
import { PublicMenuCart } from "@/components/menu/public/PublicMenuCart";
import { usePublicMenu } from "@/hooks/usePublicMenu";
import { useTranslation } from "react-i18next";
import { getCurrentLocation } from "@/services/security/orderSecurityService";
import { Button } from "@/components/ui/button";
import { CustomerNotificationPrompt } from "../CustomerNotificationPrompt";

const PublicMenuPage = () => {
  const [searchParams] = useSearchParams();
  const tableId = searchParams.get("table");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(
    null
  );
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [showTagsFilter, setShowTagsFilter] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("menu");
  const { toast } = useToast();
  const { t } = useTranslation();
  const [locationPermission, setLocationPermission] = useState<
    "granted" | "denied" | "prompt"
  >("prompt");

  // Use our custom hook to fetch menu data
  const {
    loading,
    error,
    storeName,
    storeId,
    tableName,
    tableNumber,
    menuItems,
    categories,
    activeOrders,
    availableTags,
    refreshActiveOrders,
  } = usePublicMenu(tableId);

  // Set up realtime subscription for orders
  useEffect(() => {
    if (!tableId) return;

    const channel = supabase
      .channel("public:orders:" + tableId)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `table_id=eq.${tableId}`,
        },
        (payload) => {
          refreshActiveOrders();
        }
      )
      .subscribe();

    // Also subscribe to order_items changes to catch updates to existing orders
    const itemsChannel = supabase
      .channel("public:order_items:" + tableId)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "order_items",
          filter: `order_id=in.(select id from orders where table_id='${tableId}')`,
        },
        () => {
          refreshActiveOrders();
        }
      )
      .subscribe();

    // Cleanup function to remove the subscription
    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(itemsChannel);
    };
  }, [tableId, refreshActiveOrders]);

  // Check for active orders on mount and switch tabs if needed
  useEffect(() => {
    if (activeOrders && activeOrders.length > 0) {
      setActiveTab("order");
    }
  }, [activeOrders]);

  // Update the useEffect for location permission
  useEffect(() => {
    const checkAndRequestLocation = async () => {
      if ("geolocation" in navigator) {
        try {
          // First check current permission status
          if ("permissions" in navigator) {
            const permission = await navigator.permissions.query({
              name: "geolocation",
            });
            setLocationPermission(permission.state);

            // Listen for permission changes
            permission.addEventListener("change", () => {
              setLocationPermission(permission.state);
            });

            // If permission is prompt, automatically request location
            if (permission.state === "prompt") {
              requestLocationPermission();
            }
          } else {
            // For browsers that don't support permissions API, try requesting directly
            requestLocationPermission();
          }
        } catch (error) {
          console.warn("Could not handle location permission:", error);
        }
      }
    };

    checkAndRequestLocation();
  }, []);

  const handleViewItemDetails = (item: MenuItem) => {
    setSelectedMenuItem(item);
    setShowItemDetails(true);
  };

  // Remove the handleAddToCart function - let PublicMenuCart handle all cart operations
  const handleAddToCart = (item: MenuItem) => {
    // This function will be passed to PublicMenuCart to handle
    // We'll show a basic toast here and let PublicMenuCart handle the actual cart logic
    toast({
      title: t("addedToCart"),
      description: `${item.name} ${t("hasBeenAddedToYourCart")}`,
      duration: 2000,
    });
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Handle order placement - switch to the order tab
  const handleOrderPlaced = (order: Order) => {
    refreshActiveOrders();
    setActiveTab("order");
  };

  // Update the requestLocationPermission function
  const requestLocationPermission = async () => {
    try {
      // This will trigger the browser's permission prompt
      await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      setLocationPermission("granted");
      toast({
        title: t("locationAccess.granted"),
        description: t("locationAccess.thankYou"),
        duration: 3000,
      });
    } catch (error) {
      setLocationPermission("denied");
      toast({
        title: t("locationAccess.denied"),
        description: t("locationAccess.pleaseEnable"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Add location permission banner if needed
  const renderLocationBanner = () => {
    if (locationPermission === "prompt" || locationPermission === "denied") {
      return (
        <div className="fixed bottom-0 left-0 right-0 mx-4 bg-primary text-primary-foreground p-4 rounded-lg shadow-lg z-50">
          <div className="flex flex-col md:flex-col items-start gap-4">
            <div className="flex-1 flex flex-row items-center gap-2">
              <h3 className="font-semibold">{t("locationAccess.required")}</h3>
              <Button
                variant="secondary"
                size="sm"
                onClick={requestLocationPermission}
              >
                {t("locationAccess.enable")}
              </Button>
            </div>

            <p className="text-sm mt-1">{t("locationAccess.explanation")}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <PublicMenuLoading />;
  }

  if (error) {
    return <PublicMenuError error={error} tableId={tableId} />;
  }

  // Modified to filter out delivered and completed orders
  const pendingOrders =
    activeOrders?.filter(
      (order) => order.status !== "delivered" && order.status !== "completed"
    ) || [];

  // Updated to check for any non-cancelled, non-delivered, and non-completed orders
  const hasActiveOrders =
    pendingOrders &&
    Array.isArray(pendingOrders) &&
    pendingOrders.filter((order) => order?.status !== "cancelled").length > 0;

  return (
    <div className="container mx-auto py-8 pb-24">
      <PublicMenuHeader
        storeName={storeName}
        tableName={tableName}
        tableNumber={tableNumber}
      />

      {/* Customer Notification Prompt */}
      {tableId && (
        <CustomerNotificationPrompt 
          tableId={tableId}
          onNotificationEnabled={() => {
            toast({
              title: t('notificationsEnabled'),
              description: t('youWillReceiveOrderUpdates'),
              duration: 3000,
            });
          }}
        />
      )}

      {/* Main Tabs */}
      <PublicMenuTabs activeTab={activeTab} setActiveTab={setActiveTab}>
        {/* Menu Tab Content */}
        {activeTab === "menu" ? (
          <MenuContentTab
            menuItems={menuItems}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedTags={selectedTags}
            categories={categories}
            onViewDetails={handleViewItemDetails}
            onAddToCart={handleAddToCart}
            setShowTagsFilter={setShowTagsFilter}
          />
        ) : (
          <OrderContentTab
            pendingOrders={pendingOrders}
            hasActiveOrders={hasActiveOrders}
            activeOrders={activeOrders}
            storeId={storeId || ""}
            tableId={tableId || ""}
            storeName={storeName}
            setActiveTab={setActiveTab}
          />
        )}
      </PublicMenuTabs>

      {/* Menu Item Details Dialog */}
      <MenuItemDetailsDialog
        open={showItemDetails}
        onOpenChange={setShowItemDetails}
        item={selectedMenuItem}
        onAddToCart={handleAddToCart}
      />

      {/* Tags Filter Dialog */}
      <TagsFilterDialog
        isOpen={showTagsFilter}
        onOpenChange={setShowTagsFilter}
        availableTags={availableTags}
        selectedTags={selectedTags}
        onToggleTag={toggleTag}
        onClearAll={() => setSelectedTags([])}
      />

      {/* Shopping Cart */}
      {tableId && storeId && tableName && tableNumber !== null && (
        <PublicMenuCart
          tableId={tableId}
          tableName={tableName}
          tableNumber={tableNumber}
          storeId={storeId}
          onOrderPlaced={handleOrderPlaced}
        />
      )}

      {/* Add the location banner */}
      {renderLocationBanner()}
    </div>
  );
};

export default PublicMenuPage;
